import { useHistory } from 'react-router-dom';
import React, { useMemo, useRef } from 'react';
import BroadageWidget from '../../components/BroadageWidget';
import PropTypes from 'prop-types';
import Icon from '../../components/Icon';
import BreadCrumb from './BreadCrumb';
import { useSelector } from 'react-redux';
import ToogleSeoText from '../../components/ToogleSeoText';
import { withMixpanel } from 'Utils/mixpanel';
import { isWebviewMode } from 'Utils/webviewMode';
import EnvironmentManager from '../../../utils/environmentManager';
import ProjectConfig from 'ProjectConfig';
import { applyThemeToWidgetUrl } from 'Utils/applyThemeToWidgetUrl';
import useWidgetThemeSync from 'Components/Theme/useWidgetThemeSync';

const WorldCupBracketIframe = ({ season }) => {
  const iframeRef = useRef(null);
  const { pushTheme, theme } = useWidgetThemeSync(iframeRef);
  const initialThemeRef = useRef(theme);
  const src = useMemo(() => applyThemeToWidgetUrl(
    `${ProjectConfig.CONTENT_URL}/world-cup-bracket/?season=${season}`,
    initialThemeRef.current
  ), [season]);

  return (
    <iframe
      ref={ iframeRef }
      src={ src }
      title="2026 Dünya Kupası Eşleşmeler"
      onLoad={ pushTheme }
      style={ {
        width: 'calc(100% + 16px)',
        minHeight: 720,
        border: 0,
        marginLeft: -8,
        marginTop: 15,
        backgroundColor: 'var(--background-primary-background)',
      } }
    />
  );
};

WorldCupBracketIframe.propTypes = {
  season: PropTypes.string,
};

export const LeagueContent = ({ data, match, mixpanel, isSideNavOpen, setIsSideNavOpen }) => {
  const { mainSlug, tabSlug } = match.params;
  const userAgent = useSelector(state => state.userAgent);
  const history = useHistory();
  const isMobile = userAgent?.device?.type === 'mobile';
  // Tab değişiminde URL'deki webview parametresi kaybolsa bile (bkz.
  // handleTabChange) isWebviewMode kalıcı flag'e düşer; aksi halde bu
  // false'a dönüp .league-content__header-row 'desktopview' class'ına
  // geçiyor ve mobilde margin-top: 61px ile header aşağı kayıyordu.
  const isWebView = isWebviewMode(history?.location?.search);

  const normalizeSlug = str =>
    (str || '')
      .replace(/^\/lig-merkezi\//, '')
      .replace(/İ/g, 'i')
      .replace(/Ç/g, 'c')
      .replace(/Ğ/g, 'g')
      .replace(/I/g, 'i')
      .replace(/Ö/g, 'o')
      .replace(/Ş/g, 's')
      .replace(/Ü/g, 'u')
      .toLowerCase()
      .replace(/ç/g, 'c')
      .replace(/ğ/g, 'g')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ş/g, 's')
      .replace(/ü/g, 'u')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const { foundLeague, foundTeam, sportType, isTeam, teamSlug, widgetTabs } = useMemo(() => {
    let foundLeague = null;
    let foundTeam = null;
    let sportType = 1;
    let isTeam = false;
    let teamSlug = '';
    let widgetTabs = [];

    data?.leagues?.forEach(category => {
      if (!category.leagues) return;
      category.leagues.forEach(league => {
        const leagueSlug = normalizeSlug(league.slug);
        if (leagueSlug === normalizeSlug(mainSlug)) {
          foundLeague = league;
          sportType = category.sportType || 1;
        }
        if (league.teams) {
          // eslint-disable-next-line max-nested-callbacks
          league.teams.forEach(team => {
            const normalizedTeamSlug = normalizeSlug(team.slug);
            if (normalizedTeamSlug === normalizeSlug(mainSlug)) {
              isTeam = true;
              teamSlug = normalizedTeamSlug;
              foundTeam = team;
              foundLeague = league;
              sportType = category.sportType || 1;
            }
          });
        }
      });
    });
    if (foundTeam && foundTeam.widgets && foundTeam.widgets.length > 0) {
      widgetTabs = foundTeam.widgets;
    } else if (foundLeague && foundLeague.widgets) {
      widgetTabs = foundLeague.widgets;
    }
    widgetTabs = widgetTabs.sort((a, b) => (a.order || 99) - (b.order || 99));
    return { foundLeague, foundTeam, sportType, isTeam, teamSlug, widgetTabs };
  }, [data, mainSlug]);

  const getTabSlug = tab => {
    const rawSlug = tab?.slug;
    if (typeof rawSlug === 'string' && rawSlug.trim()) {
      const cleanSlug = rawSlug.replace(/\/+$/g, '');
      const slugLastSegment = cleanSlug.split('/').filter(Boolean).pop();
      if (slugLastSegment) {
        return normalizeSlug(slugLastSegment);
      }
    }

    return normalizeSlug(tab?.name);
  };

  const activeWidget = useMemo(() => {
    if (!widgetTabs || widgetTabs.length === 0) return null;
    if (tabSlug) {

      return widgetTabs.find(tab => getTabSlug(tab) === normalizeSlug(tabSlug)) || widgetTabs[0];
    }

    return widgetTabs[0];
  }, [widgetTabs, tabSlug]);

  React.useEffect(() => {
    if (!activeWidget) return;

    const widgetContainers = document.querySelectorAll('.league-card__content > div');
    if (widgetContainers.length > 0) {
      const activeContainer = Array.from(widgetContainers).find(
        container => container.style.display !== 'none'
      );
      
      if (activeContainer) {
        const originalDisplay = activeContainer.style.display;
        activeContainer.style.display = 'none';
        
        setTimeout(() => {
          activeContainer.style.display = originalDisplay;
        }, 5);
      }
    }
  }, [activeWidget, foundLeague?.tournamentId, foundTeam?.teamId, isTeam]);

  React.useEffect(() => {
    if (!widgetTabs || widgetTabs.length === 0 || tabSlug) return;
    const firstWidget = widgetTabs[0];
    const firstTabSlug = getTabSlug(firstWidget);
    if (isTeam && teamSlug) {
      history.replace(`/lig-merkezi/${mainSlug}/${firstTabSlug}`);
    } else if (foundLeague) {
      history.replace(`/lig-merkezi/${mainSlug}/${firstTabSlug}`);
    }
  }, [widgetTabs, tabSlug, isTeam, teamSlug, foundLeague, mainSlug, history]);

  React.useEffect(() => {
    if (!activeWidget) return;

    if (!isMobile) {
      mixpanel.track(isTeam ? 'Click League Center Team Tab' : 'Click League Center League Tab', {
        'Tab Name': activeWidget?.title,
        'Event Sport': foundLeague?.sportTypeName,
      });
    }
    if (isMobile) {
      mixpanel.track(isTeam ? 'Click League Center Hamburger Team Page' : 'Click League Center Hamburger Item', {
        [isTeam ? 'Team Name' : 'Page Name']: isTeam ? teamSlug : foundLeague?.title,
        'Event Sport': foundLeague?.sportTypeName,
      });
    }
  }, [activeWidget]);

  const handleTabChange = tab => {
    const tabSlugForUrl = getTabSlug(tab);
    const search = history?.location?.search || '';
    const targetUrl = `/lig-merkezi/${mainSlug}/${tabSlugForUrl}${search}`;

    history.push(targetUrl);
  };
  
  const renderTabItems = () => (
    widgetTabs.map(tab => {
      let isActive = false;
      if (tabSlug) {
        isActive = getTabSlug(tab) === normalizeSlug(tabSlug);
      } else {
        isActive = tab === widgetTabs[0];
      }
      
      const tabKey = `${tab.name}-${foundLeague?.tournamentId || ''}-${foundTeam?.teamId || foundTeam?.id || ''}`;

      return (
        <li
          key={ tabKey }
          className={ isActive ? 'active' : '' }
          onClick={ () => handleTabChange(tab) }
        >
          { tab.name }
        </li>
      );
    })
  );

  const renderWidgetContainers = () => (

    widgetTabs.map(tab => {
      let isActive = false;
      if (tabSlug) {
        isActive = getTabSlug(tab) === normalizeSlug(tabSlug);
      } else {
        isActive = tab === widgetTabs[0];
      }

      if (tab.isTournamentBracket) {
        let season = EnvironmentManager.getMessageText({ key: 'world_cup_bracket_season' });

        return (
          <div
            style={ { display: isActive ? 'block' : 'none', overflow: 'hidden' } }
            key={ `${tab.slug}-${foundLeague?.tournamentId || ''}` }
          >
            <WorldCupBracketIframe season={ season } />
          </div>
        );
      }

      const isIstatistiks = tab.hasSubWidgets;
      let subWidgets = [];
      if (isIstatistiks && foundTeam && foundTeam.widgets && Array.isArray(tab.subWidgets)) {
        subWidgets = tab.subWidgets;
      }
      
      const widgetKey = `${tab.name}-${foundLeague?.tournamentId || ''}-${foundTeam?.teamId || foundTeam?.id || ''}`;
      
      return (
        <div
          style={ { display: isActive ? 'block' : 'none' } }
          key={ widgetKey }
        >
          { isIstatistiks && subWidgets.length > 0 ? (
            <div>
              <BroadageWidget
                sportType={ sportType }
                broadageLeagueId={ foundLeague.tournamentId }
                broadageTeamId={ foundTeam?.teamId || foundTeam?.id || null }
                type={ tab?.widgetName?.replace(/\s/g, '') }
                teamInfo={ false }
                roundNavigationRule={ tab.roundNavigationRule }
              />
              <div style={ { marginTop: 24 } }>
                { subWidgets.map((sw, idx) => (
                  <div key={ `${sw.widgetName || idx}-${foundLeague?.tournamentId || ''}-${foundTeam?.teamId || foundTeam?.id || ''}` } style={ { marginBottom: 16 } }>
                    <BroadageWidget
                      sportType={ sportType }
                      broadageLeagueId={ foundLeague.tournamentId }
                      broadageTeamId={ foundTeam?.teamId || foundTeam?.id || null }
                      type={ sw.widgetName?.replace(/\s/g, '') }
                      teamInfo={ false }
                      roundNavigationRule={ true }
                      title={ sw.name }
                      dataListHeight={ 335 }
                    />
                  </div>
                )) }
              </div>
            </div>
          ) : (
            <BroadageWidget
              sportType={ sportType }
              broadageLeagueId={ foundLeague.tournamentId }
              broadageTeamId={ foundTeam?.teamId || foundTeam?.id || null }
              type={ tab?.widgetName?.replace(/\s/g, '') }
              teamInfo={ false }
              roundNavigationRule={ tab.roundNavigationRule }
            />
          ) }
        </div>
      );
    })
  );

  return (
    <div className="league-content">
      <div className={ `league-content__header-row ${isWebView ? 'webview' : 'desktopview'}` }>
        { !isWebView
          && <span className="league-content__back" onClick={ () => {
            const search = history?.location?.search || '';
            history.push(`/lig-merkezi${search}`);
          } }>
            <Icon iconName="svgBackIcon"/>
          </span> }
        { (foundTeam?.icon || foundLeague?.icon) && (
          <img
            src={ foundTeam?.icon || foundLeague?.icon }
            alt="icon"
            className="league-content__entity-icon"
          />
        ) }
        <span className="league-content__main-title">
          { foundTeam ? foundTeam?.teamName : foundLeague?.title || foundLeague?.leagueName }
        </span>
        { isMobile && (
          <div style={ { marginLeft: 'auto' } }>
            { isSideNavOpen
              ? <Icon onClick={ () => setIsSideNavOpen && setIsSideNavOpen(false) } iconName="svgHamburgerClose" />
              : <Icon onClick={ () => setIsSideNavOpen && setIsSideNavOpen(true) } iconName="svgHamburgerMenu" />
            }
          </div>
        ) }
      </div>
      <div className="league-card__menu sliding-menu-default-theme">
        <div className="sliding-menu no-flex-grow">
          <ul>
            { renderTabItems() }
          </ul>
        </div>
      </div>
      { isMobile && !isWebView && <BreadCrumb data={ data }/> }
      <div className="league-card__content">
        { renderWidgetContainers() }
        <div className="match-card__statistics-info">
          <Icon iconName="svgInfoGrey"/>
          <div>
            İstatistik sağlayıcı firma tarafından sunulan verilerde oluşabilecek kusurlardan <span
              className="match-card__statistics-info-highlight">Bilyoner sorumlu değildir.</span>
          </div>
        </div>
        { activeWidget && (
          <ToogleSeoText
            text={ activeWidget.meta?.description }
            title={ activeWidget.meta?.title }
            extraClass="events-container__seo__height-fix"
          />
        ) }
      </div>
    </div>
  );
};

LeagueContent.propTypes = {
  data: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  mixpanel: PropTypes.object,
  isSideNavOpen: PropTypes.bool,
  setIsSideNavOpen: PropTypes.func
};

export default withMixpanel(LeagueContent);
